import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { Crown } from "lucide-react";

const COLORS = ["#FFD60A", "#C0C0C0", "#CD7F32"]; // gold, silver, bronze

/** Live top supporters leaderboard. Polls /tips/summary every 4s.
 *  Renders top 3 with crown + animated bump when ranking changes. */
export default function TopSupporters({ liveId }) {
  const [top, setTop] = useState([]);
  const prevIdsRef = useRef([]);
  const [bumpedId, setBumpedId] = useState(null);

  useEffect(() => {
    let mounted = true;
    const poll = async () => {
      try {
        const { data } = await api.get(`/lives/${liveId}/tips/summary`);
        const next = (data.top_supporters || []).slice(0, 3);
        if (!mounted) return;

        // Detect rank change → bump animation on new #1 or new entry
        const prev = prevIdsRef.current;
        const next_ids = next.map((s) => s.user_id);
        if (next.length && (prev[0] !== next_ids[0] || next_ids.some((id) => !prev.includes(id)))) {
          const bump = next_ids.find((id) => !prev.includes(id)) || next_ids[0];
          setBumpedId(bump);
          setTimeout(() => setBumpedId(null), 1200);
        }
        prevIdsRef.current = next_ids;
        setTop(next);
      } catch { /* silent */ }
    };
    poll();
    const id = setInterval(poll, 4000);
    return () => { mounted = false; clearInterval(id); };
  }, [liveId]);

  if (!top.length) return null;

  return (
    <div className="pointer-events-none absolute left-3 top-3 z-20 flex flex-col gap-1.5" data-testid="top-supporters">
      {top.map((s, i) => (
        <div
          key={s.user_id}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/70 backdrop-blur-md border ${i === 0 ? "border-[#FFD60A]/60" : "border-white/10"} shadow-lg ${bumpedId === s.user_id ? "supporter-bump" : ""}`}
          data-testid={`supporter-rank-${i + 1}`}
          style={{
            transform: `translateX(0)`,
            animation: bumpedId === s.user_id ? "supporterBump 1s cubic-bezier(0.3, 1.6, 0.5, 1)" : undefined,
          }}
        >
          <Crown className="w-3.5 h-3.5" style={{ color: COLORS[i] || "#fff" }} fill={i === 0 ? "#FFD60A" : "none"} />
          <span className="text-[10px] uppercase tracking-wider font-black" style={{ color: COLORS[i] || "#fff" }}>
            TOP {i + 1}
          </span>
          <span className="text-xs text-white font-semibold max-w-[100px] truncate">{s.user_name}</span>
          <span className="text-[11px] text-zinc-300 tabular-nums">{Intl.NumberFormat().format(s.amount)}</span>
        </div>
      ))}
    </div>
  );
}
